import { Controller } from "@hotwired/stimulus"
import {
  trapNestedModal,
  releaseNestedModal,
  getNestedModalParent,
} from "../../../utils/FocusTrap"
import { seemsOffline, redirectToOfflinePage } from "../../../utils/connectivity"
import { shouldActivateClickEvent } from "../../../utils/keyboardAccessibility"

const PRACTICE_SMART_QUESTIONS_MODAL_ROOT_ID = "openQuestionContainer"
const SAVE_QUIZ_TODO_MODAL_ID = "save-quiz-todo-modal"

export default class extends Controller {
  saveQuizTodoTrigger = null

  static targets = [
    "quizModal",
    "documentTitleQuizModal",
    "questionsAnsweredQuizModal",
    "saveQuizTodoModal",
    "quizName",
    "quizId",
  ]

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
  }

  handleKeydown(event) {
    if (this.hasSaveQuizTodoModalTarget && !this.saveQuizTodoModalTarget.classList.contains("hidden")) {
      if (event.key === "Escape") {
        event.preventDefault()
        event.stopPropagation()
        this.suppressNextEscapeKeyup()
        this.hideSaveQuizTodoModal(event)
      }
      return
    }

    if (this.railsuiModalController()?.isOpen && event.key === "Escape") {
      this.hideQuizModal(event)
    }
  }

  openQuizModal(event) {
    if (!shouldActivateClickEvent(event)) return

    event.preventDefault()
    const dataset = event.currentTarget.dataset

    this.populateQuizModalContent(dataset)

    const modalController = this.railsuiModalController()
    if (!modalController) return

    modalController.show(event)
    document.body.classList.add("modal-active")
  }

  populateQuizModalContent(dataset) {
    if (this.hasDocumentTitleQuizModalTarget) {
      const documentTitle = dataset.documentTitle || dataset.quizName || ""
      this.documentTitleQuizModalTarget.textContent = documentTitle
    }
    if (this.hasQuestionsAnsweredQuizModalTarget) {
      this.questionsAnsweredQuizModalTarget.textContent = dataset.questionsAnswered
    }

    const percentCorrect = dataset.percentageCorrect
    this.updateAccuracyChart(percentCorrect)

    const accuracySummary = document.getElementById("practice-smart-questions-modal-accuracy-summary")
    if (accuracySummary) {
      accuracySummary.textContent = `Accuracy score ${percentCorrect} percent`
    }

    const quizNameLink = document.getElementById("practice-smart-questions-modal-quiz-link")
    if (quizNameLink && dataset.quizUrl) {
      quizNameLink.href = dataset.quizUrl
    }

    const quizId = dataset.quizId
    const openQuestionLink = document.getElementById("open-question")
    if (openQuestionLink && quizId) {
      openQuestionLink.href = openQuestionLink.href.replace(/\/id\//, `/${quizId}/`)
    }

    const resetOpenQuestionLink = document.getElementById("reset-open-question")
    if (resetOpenQuestionLink && quizId) {
      resetOpenQuestionLink.href = resetOpenQuestionLink.href.replace(/\/id\//, `/${quizId}/`)
    }

    if (this.hasQuizIdTarget) {
      this.quizIdTarget.value = quizId
    }
    if (this.hasQuizNameTarget) {
      this.quizNameTarget.textContent = dataset.quizName
    }
  }

  railsuiModalController() {
    const modalRoot = document.getElementById(PRACTICE_SMART_QUESTIONS_MODAL_ROOT_ID)
    if (!modalRoot) return null

    return this.application.getControllerForElementAndIdentifier(modalRoot, "railsui-modal")
  }

  updateAccuracyChart(percentCorrect) {
    const chartEl = document.getElementById("practice-smart-questions-modal-accuracy-chart")
    if (!chartEl) return

    const chartController = this.application.getControllerForElementAndIdentifier(
      chartEl,
      "aquifer-ui-chart"
    )
    chartController?.updatePercent?.(percentCorrect)

    const centerValue = chartEl.querySelector(".aqui-chart__center-stack span")
    if (centerValue) {
      centerValue.textContent = `${percentCorrect}%`
    }
  }

  hideQuizModal(event) {
    if (this.hasSaveQuizTodoModalTarget && !this.saveQuizTodoModalTarget.classList.contains("hidden")) {
      const parentModal = this.hasQuizModalTarget ? this.quizModalTarget : getNestedModalParent()
      releaseNestedModal(parentModal, this.saveQuizTodoModalTarget, { restoreFocus: false })
      this.saveQuizTodoTrigger = null
    }

    const modalController = this.railsuiModalController()
    if (modalController?.isOpen) {
      modalController.close(event)
    }

    this.cleanupModalAccessibility()
  }

  cleanupModalAccessibility() {
    document.body.classList.remove("modal-active")
  }

  openSaveQuizTodoModal(event) {
    if (event.type === "keydown" && event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") {
      return
    }
    if (event.type === "keydown") {
      event.preventDefault()
    }
    event.preventDefault()
    event.stopPropagation()

    if (seemsOffline()) {
      redirectToOfflinePage()
      return
    }

    if (!this.hasSaveQuizTodoModalTarget) return

    this.saveQuizTodoTrigger = event.currentTarget

    const parentModal = getNestedModalParent()

    trapNestedModal(parentModal, this.saveQuizTodoModalTarget, {
      trigger: this.saveQuizTodoTrigger,
      initialFocusSelector: `#${SAVE_QUIZ_TODO_MODAL_ID} .save-quiz-todo-modal__select`,
    })
  }

  hideSaveQuizTodoModal(event) {
    const triggerElement = event?.currentTarget
    const isSubmitControl =
      triggerElement &&
      ((triggerElement.tagName === "INPUT" && triggerElement.type === "submit") ||
        (triggerElement.tagName === "BUTTON" && triggerElement.type === "submit"))

    if (isSubmitControl) {
      return
    }

    if (event) {
      event.preventDefault()
    }

    if (!this.hasSaveQuizTodoModalTarget) return

    const parentModal = getNestedModalParent()

    releaseNestedModal(parentModal, this.saveQuizTodoModalTarget)

    if (this.saveQuizTodoModalTarget.dataset.redirectTo) {
      window.location = this.saveQuizTodoModalTarget.dataset.redirectTo
      return
    }

    if (!parentModal) {
      this.cleanupModalAccessibility()
    }

    this.saveQuizTodoTrigger = null
  }

  suppressNextEscapeKeyup() {
    const suppress = (event) => {
      if (event.key === "Escape" || event.keyCode === 27) {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
      }
      window.removeEventListener("keyup", suppress, true)
    }

    window.addEventListener("keyup", suppress, true)
  }
}
